import { GroupIds } from "@/configuration/group-ids";
import { getGroupConfigSubdomain } from "@/configuration/paths";
import { ConfigSubdomain } from "@/configuration/tenant-ids";
import { Group } from "@/src/models/group";

// Groups that store MRN in the `ehrId` column and EHR ID in `secondaryEhrId`
// (post-#4882 migration). Every other group keeps EHR ID in `ehrId` and MRN
// in `secondaryEhrId`.
export const MRN_PRIMARY_SUBDOMAINS = new Set<string>([
  ConfigSubdomain.AXIA,
  ConfigSubdomain.ENT_PARTNERS,
  ConfigSubdomain.ENT_PARTNERS_MENT,
  ConfigSubdomain.ENT_PARTNERS_ENTS,
  ConfigSubdomain.ENT_PARTNERS_SCI,
  ConfigSubdomain.ENT_PARTNERS_SUBOTO,
  ConfigSubdomain.USHV_HEARTPLACE,
  ConfigSubdomain.USHV_HEARTLAND,
  ConfigSubdomain.USHV_CCOH,
  ConfigSubdomain.USHV_MCA,
  ConfigSubdomain.USHV_MKC,
  ConfigSubdomain.USHV_FORTWORTH,
  ConfigSubdomain.USHV_PIMA,
  ConfigSubdomain.PINNACLE,
  ConfigSubdomain.PA_EAST_REGION,
  ConfigSubdomain.PA_CENTRAL_REGION,
]);

// MRN-primary groups whose stored data is normalized, so the column layout is authoritative and
// legacy numeric-shape swap recovery must not run (e.g. eCW AccountNos can be alphanumeric).
const TRUSTED_COLUMN_LAYOUT_SUBDOMAINS = new Set<string>([
  ConfigSubdomain.AXIA,
  ConfigSubdomain.ENT_PARTNERS,
  ConfigSubdomain.ENT_PARTNERS_SCI,
  ConfigSubdomain.PINNACLE,
]);

// Groups that keep the natural layout (EHR ID in `ehrId`) although their configuration subdomain is
// MRN-primary. The Axia sandbox shares Axia's subdomain but runs on the Valerie mock EHR, whose
// integration fixtures store the patient's EHR ID in `ehrId`; read as MRN-primary, its fixture patients
// would have no EHR ID. A shared subdomain can't tell the two groups apart, so this keys on group ID.
const NATURAL_LAYOUT_GROUP_IDS = new Set<string>([GroupIds.AXIA_LIVE_SANDBOX]);

export function trustsColumnLayout(group: Group): boolean {
  return (
    !NATURAL_LAYOUT_GROUP_IDS.has(group.id) && TRUSTED_COLUMN_LAYOUT_SUBDOMAINS.has(getGroupConfigSubdomain(group))
  );
}

export function isMrnPrimary(group: Group): boolean {
  return !NATURAL_LAYOUT_GROUP_IDS.has(group.id) && MRN_PRIMARY_SUBDOMAINS.has(getGroupConfigSubdomain(group));
}

/**
 * Maps logical identifiers to DB columns per the group's storage convention.
 *
 * @param group - The group whose column convention to apply.
 * @param ehrSystemId - The EHR system's primary identifier (UUID / FHIR ID / Athena patientid).
 * @param mrn - The medical record number.
 * @returns Column-layout object: MRN-primary groups store MRN in `ehrId` and EHR ID in `secondaryEhrId`; all others are the reverse.
 */
export function normalizeIdentifiers(
  group: Group,
  ehrSystemId: string | null,
  mrn?: string | null
): { ehrId: string | null; secondaryEhrId: string | null } {
  if (isMrnPrimary(group)) {
    return { ehrId: mrn ?? null, secondaryEhrId: ehrSystemId };
  }

  return { ehrId: ehrSystemId, secondaryEhrId: mrn ?? null };
}
